import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

import { exploreAPI, projectAPI, imageAPI, environmentAPI, createQueryKey } from '../services/api';
import MapModal from './MapModal';
import EnvironmentSelector from './EnvironmentSelector';
import ObjectivesList from './ObjectivesList';
import { canDeleteProject } from '../utils/permissions';
import { useAuth } from '../contexts/AuthContext';
import { useMobile, useKeyboard } from '../hooks/useMobile';
import tagsData from '../data/tags.json';

// Stable, memoized collapsible section component to prevent remounts on each re-render
const CollapsibleSection = React.memo(({ 
  title,
  subtitle,
  children,
  isRequired = false,
  isExpanded,
  canToggle = true,
  onToggle,
  hasProgress,
  isMobile
}) => {
  const contentId = `section-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className={`
      transition-all duration-200 ease-out overflow-hidden rounded-xl
      ${isExpanded 
        ? 'bg-white dark:bg-gray-800/80 ring-1 ring-gray-200 dark:ring-gray-700 shadow-sm' 
        : 'bg-gray-50/80 dark:bg-gray-800/40 ring-1 ring-gray-100 dark:ring-gray-700/50 hover:ring-gray-200 dark:hover:ring-gray-600/50 hover:bg-gray-50 dark:hover:bg-gray-800/60'
      }
    `}>
      <button
        type="button"
        onClick={() => canToggle && onToggle?.()}
        aria-expanded={!!isExpanded}
        aria-controls={contentId}
        className={`w-full px-4 py-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset transition-colors ${
          canToggle ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {canToggle && (
              <div className={`
                flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center transition-all duration-200
                ${isExpanded 
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' 
                  : 'bg-gray-100 dark:bg-gray-700/60 text-gray-400 dark:text-gray-500'}
              `}>
                <ChevronRightIcon 
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    isExpanded ? 'rotate-90' : ''
                  }`} 
                />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h3 className={`font-medium text-sm transition-colors truncate ${
                  isExpanded ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {title}
                </h3>
                {isRequired && <span className="text-red-500 text-xs font-medium">*</span>}
              </div>
              {subtitle && (
                <p className={`text-xs mt-0.5 transition-colors truncate ${
                  isExpanded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {hasProgress && (
            <div className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full transition-colors ${
              isExpanded
                ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                : 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
            }`}>
              <CheckCircleIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Done</span>
            </div>
          )}
        </div>
      </button>
      
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              duration: isMobile ? 0.15 : 0.2,
              ease: [0.4, 0, 0.2, 1],
              height: { duration: isMobile ? 0.15 : 0.2 },
              opacity: { duration: isMobile ? 0.1 : 0.12 }
            }}
            className="overflow-hidden"
            style={{ willChange: isMobile ? 'transform' : 'height' }}
          >
            <div className="px-4 pb-5 pt-1 space-y-5 border-t border-gray-100 dark:border-gray-700/40">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const EditProjectModal = React.memo(({ 
  isOpen, 
  onClose, 
  project, 
  onSuccess,
  mode = 'edit', // 'edit' or 'create'
  apiType = 'explore' // 'explore' or 'project' - determines which API to use
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useMobile();
  const { isKeyboardOpen } = useKeyboard();
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Check if we're in create mode
  const isCreateMode = mode === 'create';

  // Form state - Initialize empty, let useEffect handle proper initialization
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: [],
    geolocation: null,
    difficulty: '',
    estimatedDuration: '',
    elevationGain: '',
    trailLength: { value: '', unit: 'km' },
    images: [],
    prompt: '',
    environments: [],
    objectives: [],
    settings: { archetype: 'site' } // Default to 'site' (Ongoing) for new projects
  });


  // Tag selection state
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [showTagSelector, setShowTagSelector] = useState(false);
  const tagSelectorRef = useRef(null);
  const [tagDropdownPosition, setTagDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // Image reordering state
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  
  // Map modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Section collapse states for progressive disclosure
  const [sectionsExpanded, setSectionsExpanded] = useState({
    basic: true,
    location: false,
    environments: false,
    objectives: false,
    media: false,
    advanced: false // Collapsed by default since AI prompt is optional
  });

  // Mobile full-screen modal states
  const [showEnvironmentsModal, setShowEnvironmentsModal] = useState(false);
  const [showObjectivesModal, setShowObjectivesModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);

  // Track initialization to prevent re-initialization during typing
  const initialized = useRef(false);
  const lastInitKey = useRef(null);

  // Initialize form data ONLY when modal first opens or switches modes
  useEffect(() => {
    const currentKey = `${isOpen}-${mode}-${project?.id || 'null'}`;
    
    if (isOpen && lastInitKey.current !== currentKey) {
      if (mode === 'create') {
        setFormData({
          name: '',
          description: '',
          tags: [],
          geolocation: null,
          difficulty: '',
          estimatedDuration: '',
          elevationGain: '',
          trailLength: { value: '', unit: 'km' },
          images: [],
          prompt: '',
          environments: [],
          objectives: [],
          settings: { archetype: 'site' } // Default to 'site' (Ongoing) for new projects
        });
      } else if (mode === 'edit' && project) {
        setFormData({
          name: project.name || '',
          description: project.description || '',
          tags: project.tags || [],
          geolocation: project.geolocation || null,
          difficulty: project.difficulty || '',
          estimatedDuration: project.estimatedDuration || '',
          elevationGain: project.elevationGain || '',
          trailLength: project.trailLength || { value: '', unit: 'km' },
          images: project.images || [],
          prompt: project.prompt || '',
          environments: project.environments || [],
          // Ensure objectives have valid, sequential order indices to prevent key collisions in builder
          objectives: (project.objectives || []).slice()
            .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
            .map((obj, idx) => ({ ...obj, orderIndex: idx })),
          settings: project.settings || { archetype: 'site' } // Preserve existing settings or default to 'site' (Ongoing)
        });
      }
      
      lastInitKey.current = currentKey;
      initialized.current = true;
    } else if (!isOpen) {
      // Reset when modal closes
      lastInitKey.current = null;
      initialized.current = false;
    }
  }, [isOpen, mode, project]); // Include project dependency

  // Fetch environments and objectives for mobile preview lookups
  const { data: environmentsResponse } = useQuery({
    queryKey: ['environments'],
    queryFn: () => environmentAPI.getAll({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled: isOpen, // Only fetch when modal is open
    refetchOnMount: false // Prevent refetch on re-mount
  });

  const environments = useMemo(() => 
    environmentsResponse?.data?.data?.environments || [], 
    [environmentsResponse]
  );
  
  // Lookup functions for mobile previews
  // eslint-disable-next-line no-unused-vars
  const getEnvironmentName = useCallback((environmentId) => {
    // Handle case where environments data might not be loaded yet
    if (!environments || environments.length === 0) {
      return `Environment (ID: ${environmentId})`;
    }
    const env = environments.find(e => e.id === environmentId);
    return env?.name || `Environment (ID: ${environmentId})`;
  }, [environments]);

  const getObjectiveTitle = useCallback((objectiveName) => {
    return objectiveName || null; // Now we have objectiveName directly
  }, []);

  // Helper functions for section management
  const toggleSection = useCallback((sectionKey) => {
    // Only preserve scroll position if not on mobile or if user is not actively typing
    const scrollContainer = scrollContainerRef.current;
    const shouldPreserveScroll = !isMobile && scrollContainer;
    const currentScrollTop = shouldPreserveScroll ? scrollContainer.scrollTop : 0;
    
    setSectionsExpanded(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
    
    // Gentle scroll preservation - only on desktop and only once after animation
    if (shouldPreserveScroll) {
      setTimeout(() => {
        if (scrollContainer && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
          scrollContainer.scrollTop = currentScrollTop;
        }
      }, 300);
    }
  }, [isMobile]);

  const getSectionProgress = useCallback((sectionKey) => {
    switch (sectionKey) {
      case 'basic':
        return formData.name.trim() && formData.description.trim() && formData.settings?.archetype;
      case 'location':
        return formData.geolocation !== null;
      case 'environments':
        return formData.environments.length > 0;
      case 'objectives':
        return formData.objectives.length > 0;
      case 'media':
        return formData.images.length > 0;
      case 'advanced':
        return formData.prompt.trim().length > 0; // Only complete if prompt has content
      default:
        return false;
    }
  }, [formData]);


  // Helper to compute section props once per render
  const getSectionProps = useCallback((sectionKey, options = {}) => ({
    isExpanded: options.forceExpanded ? true : sectionsExpanded[sectionKey],
    canToggle: !options.forceExpanded,
    onToggle: () => !options.forceExpanded && toggleSection(sectionKey),
    hasProgress: getSectionProgress(sectionKey),
    isMobile
  }), [sectionsExpanded, getSectionProgress, toggleSection, isMobile]);

  // Determine which API to use based on apiType prop
  const api = apiType === 'project' ? projectAPI : exploreAPI;
  
  // Create project mutation - memoized to prevent re-creation
  const createProjectMutation = useMutation({
    mutationKey: ['create-project'],
    mutationFn: (data) => api.create(data),
    onSuccess: (response) => {
      const newProject = response.data?.data?.project || response.data?.project || response.data;
      if (newProject) {
        
        // Invalidate appropriate cache based on API type
        if (apiType === 'project') {
          queryClient.invalidateQueries(['user-projects']);
          queryClient.invalidateQueries(['projects']);
        } else {
          queryClient.invalidateQueries(['explore-projects']);
        }
        
        onSuccess?.(newProject);
        onClose();
        
        // Navigate based on API type
        if (apiType === 'project') {
          navigate(`/projects/${newProject.id}`);
        } else {
          navigate(`/starters/${newProject.id}`);
        }
      }
    },
    onError: (error) => {
      toast.error('Failed to create project: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationKey: ['update-project', project?.id],
    mutationFn: (data) => api.update(project.id, data),
    onSuccess: (response) => {
      
      const updatedProject = response.data?.data?.project || response.data?.project || response.data;
      
      // Update cache based on API type
      if (apiType === 'project') {
        // Invalidate user project queries
        queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
        queryClient.invalidateQueries({ queryKey: ['user-projects'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      } else {
        // Update the specific project detail cache
        queryClient.invalidateQueries(['explore-project', project.id]);
        
        // Update the project in all explore projects caches without refetching
        queryClient.getQueryCache().findAll(['explore-projects']).forEach((query) => {
          queryClient.setQueryData(query.queryKey, (oldData) => {
            if (!oldData?.pages || !updatedProject) return oldData;
            
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                data: {
                  ...page.data,
                  data: {
                    ...page.data.data,
                    projects: page.data.data.projects?.map(p => 
                      p.id === updatedProject.id ? updatedProject : p
                    ) || []
                  }
                }
              }))
            };
          });
        });
      }
      
      onSuccess?.(updatedProject);
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to update project: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Handle form field changes
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle trail length changes
  const handleTrailLengthChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      trailLength: {
        ...prev.trailLength,
        [field]: value
      }
    }));
  }, []);

  // Handle adding tags
  const handleAddTag = (tagId) => {
    if (!formData.tags.includes(tagId)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagId]
      }));
    }
  };

  // Handle removing tags
  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Filter available tags based on search and already selected
  const availableTags = tagsData.tags.filter(tag => {
    const matchesSearch = tag.label.toLowerCase().includes(tagSearchTerm.toLowerCase()) ||
                         tag.description.toLowerCase().includes(tagSearchTerm.toLowerCase());
    const notSelected = !formData.tags.includes(tag.id);
    return matchesSearch && notSelected;
  });

  // Calculate dropdown position when showing tag selector (desktop)
  useEffect(() => {
    if (showTagSelector && tagSelectorRef.current && !isMobile) {
      const updatePosition = () => {
        const rect = tagSelectorRef.current.getBoundingClientRect();
        setTagDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      };
      
      updatePosition();
      
      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTagSelector, isMobile]);

  // Close tag selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the button AND outside the dropdown portal
      const dropdownPortal = document.getElementById('tag-dropdown-portal');
      const isOutsideButton = tagSelectorRef.current && !tagSelectorRef.current.contains(event.target);
      const isOutsideDropdown = !dropdownPortal || !dropdownPortal.contains(event.target);
      
      if (isOutsideButton && isOutsideDropdown) {
        setShowTagSelector(false);
        setTagSearchTerm('');
      }
    };

    if (showTagSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTagSelector]);

  // Handle image upload
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const uploadedImages = [];

    try {
      for (const file of Array.from(files)) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);
        
        const response = await imageAPI.upload(formDataUpload);
        if (response.data?.data?.filename) {
          uploadedImages.push(response.data.data.filename);
        }
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));

      toast.success(`${uploadedImages.length} image(s) uploaded.`);
    } catch (error) {
      toast.error('Failed to upload images: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsUploading(false);
    }
  };


  // Handle image reordering (drag and drop)
  const handleImageDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleImageDragEnter = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the container, not just moving between children
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleImageDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) {
      setDraggedImageIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...formData.images];
    const draggedImage = newImages[draggedImageIndex];
    
    // Remove from old position
    newImages.splice(draggedImageIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);

    setFormData(prev => ({
      ...prev,
      images: newImages
    }));

    setDraggedImageIndex(null);
    setDragOverIndex(null);
  };

  // Handle removing images
  const handleRemoveImage = (indexToRemove) => {
    if (window.confirm('Are you sure you want to remove this image?')) {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, index) => index !== indexToRemove)
      }));
    }
  };

  // Handle moving images up/down
  const handleMoveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.images.length) return;
    
    const newImages = [...formData.images];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  // Handle location click
  const handleLocationClick = () => {
    setShowMapModal(true);
  };
  
  // Handle location save from map
  const handleLocationSave = (locationData) => {
    setFormData(prev => ({
      ...prev,
      geolocation: locationData
    }));
    setShowMapModal(false);
  };
  
  // Get location display text
  const getLocationDisplayText = () => {
    if (!formData.geolocation) {
      return 'Click to set location';
    }

    const trailPoints = Array.isArray(formData.geolocation)
      ? formData.geolocation
      : Array.isArray(formData.geolocation?.trail)
        ? formData.geolocation.trail
        : null;

    if (trailPoints && trailPoints.length > 0) {
      const count = trailPoints.length;
      return `Trail with ${count} waypoint${count !== 1 ? 's' : ''}`;
    }

    const latValue =
      formData.geolocation.lat ??
      formData.geolocation?.center?.lat ??
      formData.geolocation.latitude;
    const lngValue =
      formData.geolocation.lng ??
      formData.geolocation?.center?.lng ??
      formData.geolocation.longitude;

    if (Number.isFinite(latValue) && Number.isFinite(lngValue)) {
      return `Location: ${latValue.toFixed(4)}, ${lngValue.toFixed(4)}`;
    }

    return 'Location data unavailable';
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Project description is required');
      return;
    }
    
    // Log the image order and geolocation for debugging
    console.log('Submitting with images in order:', formData.images);
    console.log('Submitting with geolocation:', formData.geolocation);
    console.log('Submitting with objectives:', formData.objectives);
    
    if (formData.geolocation) {
      if (Array.isArray(formData.geolocation)) {
        console.log('Geolocation is trail with', formData.geolocation.length, 'waypoints');
      } else {
        console.log('Geolocation is single location:', formData.geolocation);
      }
    }

    // Prepare data for submission
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
      estimatedDuration: formData.estimatedDuration ? parseInt(formData.estimatedDuration) : null,
      elevationGain: formData.elevationGain ? parseInt(formData.elevationGain) : null,
      trailLength: formData.trailLength.value ? {
        value: parseFloat(formData.trailLength.value),
        unit: formData.trailLength.unit
      } : null,
      // Ensure images array maintains the current order
      images: formData.images,
      // Handle geolocation data (single point or trail)
      geolocation: formData.geolocation,
      // Include environments and objectives
      environments: formData.environments,
      objectives: formData.objectives,
      // Include settings (with archetype)
      settings: formData.settings || { archetype: 'site' }
    };

    // Use appropriate mutation based on mode
    if (isCreateMode) {
      createProjectMutation.mutate(submitData);
    } else {
      updateProjectMutation.mutate(submitData);
    }
  };

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationKey: ['delete-project', project?.id],
    mutationFn: () => api.delete(project.id),
    onSuccess: () => {
      // Remove project from appropriate caches based on API type
      if (apiType === 'project') {
        // Remove from user project caches
        queryClient.getQueryCache().findAll(['user-projects']).forEach((query) => {
          queryClient.setQueryData(query.queryKey, (oldData) => {
            if (!oldData?.pages) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                data: Array.isArray(page?.data) ? page.data.filter(p => p?.id !== project.id) : page.data
              }))
            };
          });
        });
        queryClient.removeQueries({ queryKey: createQueryKey.project(project.id) });
        onClose();
        navigate('/projects');
      } else {
        // Remove project from all explore projects caches
        queryClient.getQueryCache().findAll(['explore-projects']).forEach((query) => {
          queryClient.setQueryData(query.queryKey, (oldData) => {
            if (!oldData?.pages) return oldData;
            
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                data: {
                  ...page.data,
                  data: {
                    ...page.data.data,
                    projects: page.data.data.projects?.filter(p => p.id !== project.id) || []
                  }
                }
              }))
            };
          });
        });
        
        // Remove the deleted project from cache (don't invalidate since it no longer exists)
        queryClient.removeQueries(['explore-project', project.id]);
        
        // Close modal and navigate back to discover page
        onClose();
        navigate('/starters');
      }
    },
    onError: (error) => {
      toast.error('Failed to delete project: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Handle delete confirmation
  const handleDeleteProject = () => {
    if (!canDeleteProject(user, project)) {
      toast.error('You do not have permission to delete this project');
      return;
    }
    setShowDeleteConfirm(true);
  };

  // Confirm delete action
  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteProjectMutation.mutate();
  };

  // Animation variants for bottom slide (mobile) and fade/scale (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Animation transition settings  
  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1], // Material Design easing
    duration: isMobile ? 0.35 : 0.25, // Slightly longer for mobile slide, shorter for desktop fade
  };

  // Background transition (faster fade)
  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.2,
  };

  // Close on backdrop click (desktop only)
  const handleBackdropMouseDown = (e) => {
    if (!isMobile && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className={`fixed inset-0 bg-black bg-opacity-50 ${
            isMobile 
              ? 'z-[99999] flex items-stretch justify-stretch'
              : 'z-[9999] flex items-start justify-center p-2 sm:p-4 overflow-y-auto'
          }`}
          onMouseDown={handleBackdropMouseDown}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={backgroundTransition}
          style={{ 
            isolation: 'isolate',
            contain: 'layout style paint'
          }}
        >
          <motion.div 
            className={`bg-white dark:bg-gray-800 shadow-2xl w-full flex flex-col ${
              isMobile 
                ? 'h-screen max-h-screen relative z-[99999]' 
                : 'rounded-xl max-w-2xl my-4 max-h-[calc(100vh-2rem)] overflow-hidden ring-1 ring-black/5 dark:ring-white/5'
            }`}
            style={isMobile ? { 
              minHeight: isKeyboardOpen ? 'auto' : '100dvh',
              height: isKeyboardOpen ? 'auto' : '100dvh',
              maxHeight: isKeyboardOpen ? 'calc(100dvh - var(--keyboard-height, 0px))' : '100dvh',
              width: '100vw'
            } : {}}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
          >
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-4 sm:px-6 sm:py-5 border-b border-gray-100 dark:border-gray-700/60 flex-shrink-0 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-800/50">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${
              isCreateMode 
                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' 
                : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
            }`}>
              {isCreateMode 
                ? <PlusCircleIcon className="w-5 h-5" />
                : <PencilSquareIcon className="w-5 h-5" />
              }
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                {isCreateMode ? 'Create Project' : 'Edit Project'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden sm:block">
                {isCreateMode ? 'Set up a new project template' : 'Update project details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 ${
              isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
            }`}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>


        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div 
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto mobile-scroll p-4 sm:p-5 space-y-3" 
            style={{ 
              scrollBehavior: 'auto',
              overscrollBehavior: 'contain',
              paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 80px)' : '24px'
            }}
          >
            
            {/* Basic Information - Always visible as it's required */}
            <CollapsibleSection
              title="Basic Information"
              subtitle="Name, description, type, and tags"
              isRequired={true}
              {...getSectionProps('basic', { forceExpanded: true })}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-name">
                    Name
                  </label>
                  <input
                    key="project-name-input"
                    type="text"
                    id="project-name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input"
                    placeholder="Give your project a name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-description">
                    Description
                  </label>
                  <textarea
                    key="project-description-textarea"
                    id="project-description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input resize-none"
                    placeholder="Describe what participants will do..."
                    required
                  />
                </div>

                {/* Type */}
                {/* <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-type">
                    Type
                  </label>
                  <select
                    id="project-type"
                    value={formData.settings?.archetype || 'site'}
                    onChange={(e) => handleInputChange('settings', { ...formData.settings, archetype: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input cursor-pointer"
                  >
                    <option value="site">Ongoing</option>
                    <option value="event">One-off</option>
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-500">
                    {formData.settings?.archetype === 'event' 
                      ? 'Completes when all objectives are finished'
                      : 'Participants can start multiple sessions over time'}
                  </p>
                </div> */}

                {/* Tags */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                    Tags
                  </label>
                  <div className="space-y-2">
                    {/* Tag selector - Mobile uses full-screen modal, desktop uses dropdown */}
                    {isMobile ? (
                      <button
                        type="button"
                        onClick={() => setShowTagsModal(true)}
                        className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 transition-all text-left mobile-button no-tap-highlight touch-feedback"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">
                            {formData.tags.length > 0 ? `${formData.tags.length} tag${formData.tags.length > 1 ? 's' : ''} selected` : 'Add tags...'}
                          </span>
                          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                        </div>
                      </button>
                    ) : (
                      <>
                        <div ref={tagSelectorRef}>
                          <button
                            type="button"
                            onClick={() => setShowTagSelector(!showTagSelector)}
                            className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 transition-all text-left"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500 dark:text-gray-400">
                                {formData.tags.length > 0 ? `${formData.tags.length} tag${formData.tags.length > 1 ? 's' : ''} selected` : 'Add tags...'}
                              </span>
                              <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTagSelector ? 'rotate-90' : ''}`} />
                            </div>
                          </button>
                        </div>
                        
                        {/* Desktop dropdown rendered via portal to escape overflow:hidden */}
                        {showTagSelector && createPortal(
                          <div 
                            id="tag-dropdown-portal"
                            className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl max-h-72 overflow-hidden"
                            style={{
                              top: tagDropdownPosition.top,
                              left: tagDropdownPosition.left,
                              width: tagDropdownPosition.width,
                              zIndex: 99999
                            }}
                          >
                            {/* Search input */}
                            <div className="p-2 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800 sticky top-0">
                              <input
                                type="text"
                                placeholder="Search tags..."
                                value={tagSearchTerm}
                                onChange={(e) => setTagSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900/50 rounded-md text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                                autoFocus
                              />
                            </div>
                            
                            {/* Available tags */}
                            <div className="max-h-56 overflow-y-auto">
                              {availableTags.length === 0 ? (
                                <div className="py-6 px-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                                  {tagSearchTerm ? 'No matching tags' : 'All tags selected'}
                                </div>
                              ) : (
                                availableTags.map((tag) => (
                                  <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => {
                                      handleAddTag(tag.id);
                                      setTagSearchTerm('');
                                      setShowTagSelector(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                  >
                                    <span className="text-base flex-shrink-0">{tag.icon}</span>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 dark:text-white truncate">
                                        {tag.label}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {tag.description}
                                      </div>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </>
                    )}
                    
                    {/* Selected tags */}
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" role="list" aria-live="polite" aria-relevant="additions removals">
                        {formData.tags.map((tagId) => {
                          const tag = tagsData.tags.find(t => t.id === tagId);
                          return (
                            <span
                              key={tagId}
                              className="inline-flex items-center gap-1.5 px-1.5 md:py-1.5 py-1.5 pl-4 pr-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium group"
                              role="listitem"
                            >
                              <span className="text-sm">{tag?.icon}</span>
                              <span>{tag?.label || tagId}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tagId)}
                                className={`p-1 rounded-md hover:bg-blue-100 min-w-10 dark:hover:bg-blue-800/50 transition-colors ${isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''}`}
                                aria-label={`Remove ${tag?.label || tagId}`}
                              >
                                <XMarkIcon className="w-3.5 h-3.5 mx-auto" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Difficulty and Duration */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Difficulty */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-difficulty">
                      Difficulty
                    </label>
                    <select
                      id="project-difficulty"
                      value={formData.difficulty}
                      onChange={(e) => handleInputChange('difficulty', e.target.value)}
                      className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input cursor-pointer"
                    >
                      <option value="">Not set</option>
                      <option value="easy">Easy</option>
                      <option value="moderate">Moderate</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-duration">
                      Duration (min)
                    </label>
                    <input
                      id="project-duration"
                      type="number"
                      inputMode="numeric"
                      value={formData.estimatedDuration}
                      onChange={(e) => handleInputChange('estimatedDuration', e.target.value)}
                      className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input"
                      placeholder="e.g., 60"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </CollapsibleSection>


            {/* Media - Collapsible */}
            <CollapsibleSection
              title="Images"
              subtitle={formData.images.length > 0 ? `${formData.images.length} image${formData.images.length !== 1 ? 's' : ''}` : "Add cover and gallery photos"}
              {...getSectionProps('media')}
            >
              {/* Upload Area */}
              <div 
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className={`w-full py-6 px-4 border border-dashed border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50/50 dark:bg-gray-800/30 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all cursor-pointer flex flex-col items-center justify-center gap-2 group ${isUploading ? 'opacity-60 cursor-wait pointer-events-none' : ''}`}
              >
                 <div className={`p-2 rounded-lg transition-all ${isUploading ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-700/60 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30'}`}>
                   {isUploading ? (
                     <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                   ) : (
                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                     </svg>
                   )}
                 </div>
                 <div className="text-center">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {isUploading ? 'Uploading...' : 'Upload images'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      PNG, JPG, or WEBP
                    </p>
                 </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files)}
                className="hidden"
              />

              {/* Image Grid */}
              {formData.images.length > 0 && (
                <div className="mt-4">
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {formData.images.map((image, index) => (
                      <div
                        key={`${image}-${index}`}
                        draggable={!isMobile}
                        onDragStart={(e) => handleImageDragStart(e, index)}
                        onDragEnd={handleImageDragEnd}
                        onDragOver={handleImageDragOver}
                        onDragEnter={(e) => handleImageDragEnter(e, index)}
                        onDragLeave={handleImageDragLeave}
                        onDrop={(e) => handleImageDrop(e, index)}
                        className={`relative group aspect-square rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700 transition-all duration-150 ${
                          draggedImageIndex === index ? 'opacity-40 scale-95' : ''
                        } ${
                          dragOverIndex === index && draggedImageIndex !== index 
                            ? 'ring-2 ring-blue-500 dark:ring-blue-400 scale-[1.02]' 
                            : ''
                        } ${!isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
                      >
                        {/* Cover photo badge */}
                        {index === 0 && (
                          <div className="absolute top-1 left-1 z-10 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                            Cover
                          </div>
                        )}
                        
                        <img
                          src={require('../config/runtime').getImageUrl(image)}
                          alt={`Project gallery ${index + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Controls overlay - always visible on mobile, hover on desktop */}
                        <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end justify-center p-1.5 transition-opacity ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <div className="flex items-center gap-1">
                            {/* Move buttons */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleMoveImage(index, index - 1); }}
                              disabled={index === 0}
                              className="p-1 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move left"
                            >
                              <ChevronUpIcon className="w-3 h-3 -rotate-90" />
                            </button>
                            
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleMoveImage(index, index + 1); }}
                              disabled={index === formData.images.length - 1}
                              className="p-1 bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 rounded hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move right"
                            >
                              <ChevronDownIcon className="w-3 h-3 -rotate-90" />
                            </button>
                            
                            {/* Delete button */}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }}
                              className="p-1 bg-red-500/90 text-white rounded hover:bg-red-600 transition-colors"
                              title="Remove image"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.images.length > 1 && !isMobile && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
                      Drag to reorder • First image is the cover
                    </p>
                  )}
                </div>
              )}
            </CollapsibleSection>

            {/* Location & Route - Collapsible */}
            <CollapsibleSection
              title="Location"
              subtitle={formData.geolocation ? getLocationDisplayText() : "Pin a location or draw a route"}
              {...getSectionProps('location')}
            >
              <div className="space-y-4">
                {/* Location picker button */}
                <button
                  type="button"
                  onClick={handleLocationClick}
                  className={`w-full py-5 px-4 border border-dashed rounded-lg transition-all group flex items-center justify-center gap-3 ${
                    formData.geolocation 
                      ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10 hover:bg-green-50 dark:hover:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/30 dark:hover:bg-blue-900/10'
                  }`}
                >
                  <div className={`p-2 rounded-lg transition-all ${
                    formData.geolocation
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-500 dark:group-hover:text-blue-400'
                  }`}>
                    <MapPinIcon className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-medium transition-colors ${
                      formData.geolocation
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                    }`}>
                      {formData.geolocation ? 'Edit location' : 'Set location'}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {formData.geolocation ? getLocationDisplayText() : 'Single point or trail route'}
                    </p>
                  </div>
                  {formData.geolocation && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, geolocation: null })); }}
                      className="ml-auto p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Remove location"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </button>

                {/* Elevation & Trail Length */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-elevation">
                      Elevation (m)
                    </label>
                    <input
                      id="project-elevation"
                      type="number"
                      inputMode="numeric"
                      value={formData.elevationGain}
                      onChange={(e) => handleInputChange('elevationGain', e.target.value)}
                      className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input"
                      placeholder="e.g., 500"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="project-trail-length-value">
                      Distance
                    </label>
                    <div className="flex gap-1.5">
                      <input
                        id="project-trail-length-value"
                        type="number"
                        step="0.1"
                        inputMode="decimal"
                        value={formData.trailLength.value}
                        onChange={(e) => handleTrailLengthChange('value', e.target.value)}
                        className="flex-1 min-w-0 px-3 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input"
                        placeholder="5.2"
                        min="0"
                      />
                      <select
                        id="project-trail-length-unit"
                        aria-label="Trail length unit"
                        value={formData.trailLength.unit}
                        onChange={(e) => handleTrailLengthChange('unit', e.target.value)}
                        className="w-16 px-2 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input cursor-pointer"
                      >
                        <option value="km">km</option>
                        <option value="mi">mi</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Environments - With mobile optimization */}
            {/* <CollapsibleSection
              title="Environments"
              subtitle={formData.environments.length > 0 ? `${formData.environments.length} environment${formData.environments.length !== 1 ? 's' : ''} selected` : "Specify environment types for this project"}
              {...getSectionProps('environments')}
            >
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Specify the type of environments where this project can be conducted instead of or in addition to specific locations.
              </p>
              {isMobile ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowEnvironmentsModal(true)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-900 dark:text-white">
                        {formData.environments.length > 0 
                          ? `${formData.environments.length} environment${formData.environments.length !== 1 ? 's' : ''} selected`
                          : 'Select environments'
                        }
                      </span>
                      <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  </button>
                  {formData.environments.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1">
                      {formData.environments.map((env, index) => {
                        const environmentName = getEnvironmentName(env.environmentId);
                        // Clean display: show name if available, otherwise show "Environment #"
                        const displayName = environmentName.startsWith('Environment (ID:') 
                          ? `Environment ${index + 1}` 
                          : environmentName;
                        return (
                          <span key={index} className="text-sm px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                            {displayName}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <EnvironmentSelector
                  selectedEnvironments={formData.environments}
                  onChange={(environments) => handleInputChange('environments', environments)}
                  allowPrimary={true}
                  className="mb-4"
                />
              )}
            </CollapsibleSection> */}

            {/* Objectives - With mobile optimization */}
            <CollapsibleSection
              title="Objectives"
              subtitle={formData.objectives.length > 0 ? `${formData.objectives.length} objective${formData.objectives.length !== 1 ? 's' : ''}` : "Define goals for participants"}
              {...getSectionProps('objectives')}
            >
              {isMobile ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowObjectivesModal(true)}
                    className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 transition-all text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        {formData.objectives.length > 0 
                          ? 'Edit objectives'
                          : 'Add objectives...'}
                      </span>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                  {formData.objectives.length > 0 && (
                    <div className="space-y-1.5">
                      {formData.objectives.slice(0, 4).map((obj, index) => {
                        const objectiveTitle = getObjectiveTitle(obj.objectiveName);
                        const displayTitle = objectiveTitle || `Objective ${index + 1}`;
                        
                        return (
                          <div key={index} className="text-xs px-3 py-2 bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="truncate">{displayTitle}</span>
                          </div>
                        );
                      })}
                      {formData.objectives.length > 4 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-1">
                          +{formData.objectives.length - 4} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <ObjectivesList
                  mode="local"
                  objectives={formData.objectives}
                  onChange={(objectives) => handleInputChange('objectives', objectives)}
                  currentUser={user}
                  showHeader={false}
                  showAddButton={true}
                  className=""
                />
              )}
            </CollapsibleSection>

            {/* Advanced Settings - Collapsible */}
            <CollapsibleSection
              title="AI Prompt"
              subtitle={formData.prompt ? "Custom instructions set" : "Optional custom instructions"}
              {...getSectionProps('advanced')}
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide" htmlFor="ai-prompt">
                  Custom Instructions
                </label>
                <textarea
                  id="ai-prompt"
                  value={formData.prompt}
                  onChange={(e) => handleInputChange('prompt', e.target.value)}
                  rows={3}
                  className="w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-800/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 mobile-input resize-none"
                  placeholder="e.g., You are a nature guide that helps participants discover local wildlife..."
                />
                <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                  Customize how the AI assistant behaves during this project
                </p>
              </div>
            </CollapsibleSection>

            
            {/* Delete Section - only show in edit mode */}
            {!isCreateMode && canDeleteProject(user, project) && (
              <div className="mt-6 p-4 rounded-xl bg-red-50/50 dark:bg-red-900/10 ring-1 ring-red-100 dark:ring-red-900/30">
                <div className="flex items-center gap-3 sm:justify-between flex-col sm:flex-row">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex-shrink-0">
                      <TrashIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-900 dark:text-red-200">
                        Delete project
                      </p>
                      <p className="text-xs text-red-700/70 dark:text-red-300/60 mt-0.5">
                        Permanently remove this project and all its data
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteProject}
                    disabled={deleteProjectMutation.isLoading}
                    className={`w-full sm:w-auto px-4 py-2 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex-shrink-0 ${
                      isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
                    }`}
                  >
                    {deleteProjectMutation.isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-3.5 h-3.5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Deleting...</span>
                      </div>
                    ) : (
                      'Delete Project'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className={`flex items-center justify-end gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800 flex-shrink-0 ${
            isMobile ? 'fixed inset-x-0 bottom-0 z-[99999]' : ''
          }`} style={isMobile ? { paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' } : {}}>
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreateMode ? createProjectMutation.isLoading : updateProjectMutation.isLoading}
              className={`px-6 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white text-sm rounded-lg font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-200 active:scale-[0.98] ${
                isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
              }`}
            >
              {(isCreateMode ? createProjectMutation.isLoading : updateProjectMutation.isLoading) ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isCreateMode ? 'Creating...' : 'Saving...'}</span>
                </div>
              ) : (
                isCreateMode ? 'Create Project' : 'Save Changes'
              )}
            </button>
          </div>
        </form>
        
        {/* Map Modal */}
        {showMapModal && (
          <MapModal
            isOpen={showMapModal}
            onClose={() => setShowMapModal(false)}
            value={formData.geolocation}
            onChange={handleLocationSave}
            title="Set Project Location"
            description="Choose a single location or create a trail route for your project"
          />
        )}

        {/* Delete Confirmation Dialog */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div 
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <motion.div 
                className={`bg-white dark:bg-gray-800 rounded-xl p-5 w-full shadow-xl ring-1 ring-black/5 dark:ring-white/5 ${
                  isMobile ? 'max-w-sm' : 'max-w-md'
                }`}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex-shrink-0">
                    <ExclamationTriangleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      Delete project?
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      "{project?.name}" will be permanently deleted. This cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className={`px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                      isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleteProjectMutation.isLoading}
                    className={`px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      isMobile ? 'mobile-button no-tap-highlight touch-feedback' : ''
                    }`}
                  >
                    {deleteProjectMutation.isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Deleting...</span>
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Full-Screen Tags Modal */}
        <AnimatePresence>
          {showTagsModal && (
            <motion.div
              className="fixed inset-0 bg-white dark:bg-gray-900 z-[99999] flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 flex-shrink-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  Select Tags
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowTagsModal(false);
                    setTagSearchTerm('');
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mobile-button no-tap-highlight touch-feedback"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search input - sticky */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/40 bg-white dark:bg-gray-900 flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search tags..."
                    value={tagSearchTerm}
                    onChange={(e) => setTagSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 text-base bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                  {tagSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setTagSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Selected tags summary */}
              {formData.tags.length > 0 && (
                <div className="px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800/30 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {formData.tags.length} tag{formData.tags.length !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tags: [] }))}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
              
              {/* Tags list */}
              <div className="flex-1 overflow-y-auto mobile-scroll">
                {tagsData.tags.filter(tag => 
                  tag.label.toLowerCase().includes(tagSearchTerm.toLowerCase()) ||
                  tag.description.toLowerCase().includes(tagSearchTerm.toLowerCase())
                ).length === 0 ? (
                  <div className="py-12 px-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                      No tags match "{tagSearchTerm}"
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {tagsData.tags.filter(tag => 
                      tag.label.toLowerCase().includes(tagSearchTerm.toLowerCase()) ||
                      tag.description.toLowerCase().includes(tagSearchTerm.toLowerCase())
                    ).map((tag) => {
                      const isSelected = formData.tags.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              handleRemoveTag(tag.id);
                            } else {
                              handleAddTag(tag.id);
                            }
                          }}
                          className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-colors mobile-button no-tap-highlight touch-feedback ${
                            isSelected 
                              ? 'bg-blue-50 dark:bg-blue-900/20' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 active:bg-gray-100 dark:active:bg-gray-800'
                          }`}
                        >
                          <span className="text-2xl flex-shrink-0">{tag.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium text-base ${
                              isSelected 
                                ? 'text-blue-900 dark:text-blue-100' 
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {tag.label}
                            </div>
                            <div className={`text-sm mt-0.5 ${
                              isSelected 
                                ? 'text-blue-700/70 dark:text-blue-300/70' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {tag.description}
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center transition-all ${
                            isSelected 
                              ? 'bg-blue-600 dark:bg-blue-500' 
                              : 'border-2 border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && (
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4 text-white">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Done button */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-900 flex-shrink-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowTagsModal(false);
                    setTagSearchTerm('');
                  }}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-base font-semibold rounded-xl transition-colors mobile-button no-tap-highlight touch-feedback"
                >
                  Done {formData.tags.length > 0 && `(${formData.tags.length})`}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Full-Screen Environments Modal */}
        <AnimatePresence>
          {showEnvironmentsModal && (
            <motion.div
              className="fixed inset-0 bg-white dark:bg-gray-800 z-[99999] flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  Environments
                </h2>
                <button
                  onClick={() => setShowEnvironmentsModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <EnvironmentSelector
                  selectedEnvironments={formData.environments}
                  onChange={(environments) => handleInputChange('environments', environments)}
                  allowPrimary={true}
                  className=""
                />
              </div>
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
                <button
                  onClick={() => setShowEnvironmentsModal(false)}
                  className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Full-Screen Objectives Modal */}
        <AnimatePresence>
          {showObjectivesModal && (
            <motion.div
              className="fixed inset-0 bg-white dark:bg-gray-800 z-[99999] flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  Objectives
                </h2>
                <button
                  onClick={() => setShowObjectivesModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ObjectivesList
                  mode="local"
                  objectives={formData.objectives}
                  onChange={(objectives) => handleInputChange('objectives', objectives)}
                  currentUser={user}
                  showHeader={false}
                  showAddButton={true}
                  className=""
                />
              </div>
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/60" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
                <button
                  onClick={() => setShowObjectivesModal(false)}
                  className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default EditProjectModal;
